import type React from "react";
import { useEffect, useRef } from "react";
import { Button } from "@design-system/Button/Button";
import { ArrowRightIcon, LeftArrowIcon } from "@icons";
import type { PanDirection } from "./densityViewport.lib";
import styles from "./DensityChart.module.scss";

interface DensityChartPanControlsProps {
    canPanEarlier: boolean;
    canPanLater: boolean;
    /** The left fade's strength, from 0 to 1. */
    earlierFade: number;
    /** Pans that way and says whether there is still more to see that way. */
    onPan: (direction: PanDirection) => boolean;
}

// ds-deviation: Figma's arrows are Ghost; Daniel chose Primary so they stay visible over the bars.
const ARROW_VARIANT = "primary";

const OTHER_DIRECTION: Record<PanDirection, PanDirection> = {
    earlier: "later",
    later: "earlier",
};

/**
 * The arrows over the bars' ends, each shown only while at least a bar is
 * hidden that way, and a fade on the left as strong as `earlierFade`. A
 * focused arrow that hides at the end hands its focus to the other one.
 */
export function DensityChartPanControls({
    canPanEarlier,
    canPanLater,
    earlierFade,
    onPan,
}: DensityChartPanControlsProps) {
    const earlierRef = useRef<HTMLButtonElement>(null);
    const laterRef = useRef<HTMLButtonElement>(null);
    const focusTargetRef = useRef<PanDirection | null>(null);
    const handlePan =
        (direction: PanDirection) =>
        (event: React.MouseEvent<HTMLButtonElement>) => {
            const hadFocus = document.activeElement === event.currentTarget;
            const canPanFurther = onPan(direction);
            focusTargetRef.current =
                hadFocus && !canPanFurther ? OTHER_DIRECTION[direction] : null;
        };
    // `onPan` says it can't pan further exactly when that arrow is about to
    // hide, so the hand-off runs once the arrows have changed.
    useEffect(() => {
        const target = focusTargetRef.current;
        focusTargetRef.current = null;
        if (target) {
            (target === "earlier" ? earlierRef : laterRef).current?.focus();
        }
    }, [canPanEarlier, canPanLater]);
    return (
        <>
            {earlierFade > 0 && (
                <div
                    className={styles.earlierFade}
                    style={{ opacity: earlierFade }}
                    aria-hidden
                />
            )}
            {canPanEarlier && (
                <Button
                    ref={earlierRef}
                    variant={ARROW_VARIANT}
                    size="md"
                    iconOnly
                    className={styles.panEarlier}
                    aria-label="Show earlier activity"
                    onClick={handlePan("earlier")}
                >
                    <LeftArrowIcon
                        className="icon-color-gray-900"
                        aria-hidden
                    />
                </Button>
            )}
            {canPanLater && (
                <Button
                    ref={laterRef}
                    variant={ARROW_VARIANT}
                    size="md"
                    iconOnly
                    className={styles.panLater}
                    aria-label="Show later activity"
                    onClick={handlePan("later")}
                >
                    <ArrowRightIcon
                        className="icon-color-gray-900"
                        aria-hidden
                    />
                </Button>
            )}
        </>
    );
}
